import { Button } from '@ads/components-react';
import { brandLinka } from '@ads/tokens';
import { DashboardSalesByStatus } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { useNavigate } from 'react-router';
import { Pie, PieChart } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../chart/chart';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
} from '../components';
import { useSearchFilters } from '../hooks/use-search-filters';

interface SalesByStatusProps {
  data: DashboardSalesByStatus;
}
export const SalesByStatusChart = ({ data }: SalesByStatusProps) => {
  const navigate = useNavigate();
  const t = useTranslation();
  const { deleteFilter } = useSearchFilters();
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sales-status')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_SALES_BY_STATUS' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent>
          <ChartContainer
            config={{
              COMPLETE: { label: t('COMPLETE'), color: brandLinka.baseColor.primary['400'] },
              EXPIRED: { label: t('EXPIRED'), color: brandLinka.baseColor.gray['400'] },
              OPEN: { label: t('OPEN'), color: brandLinka.baseColor.warning['400'] },
              FAILED: { label: t('FAILED'), color: brandLinka.baseColor.danger['600'] },
              AWAITING_PAYMENT: { label: t('AWAITING_PAYMENT'), color: brandLinka.baseColor.primary['200'] },
              PARTIALLY_REFUNDED: { label: t('PARTIALLY_REFUNDED'), color: brandLinka.baseColor.brand['300'] },
              FULLY_REFUNDED: { label: t('FULLY_REFUNDED'), color: brandLinka.baseColor.danger['400'] },
              CANCELLED: { label: t('CANCELLED'), color: brandLinka.baseColor.danger['400'] },
            }}
            className='min-h-[200px] w-full'
          >
            <PieChart
              data={data.data.map((item) => ({
                ...item,
                fill: `var(--color-${item.status})`,
              }))}
              responsive
              accessibilityLayer
            >
              <Pie innerRadius={50} dataKey='totalOrders' nameKey='status' minAngle={10} label />
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <ChartLegend content={<ChartLegendContent />} />
            </PieChart>
          </ChartContainer>
        </ChartCardContent>
      )}
      {data.data.length > 0 && (
        <ChartCardFooter className='flex flex-wrap *:!w-full sm:*:!w-auto gap-nano justify-end pt-mini'>
          <Button
            className='whitespace-nowrap !text-xxxs sm:!text-xxs'
            onClick={() => {
              deleteFilter('status');
              navigate(`detailed-order-status?status=AWAITING_PAYMENT`);
            }}
          >
            {t('dashboard.detailed.awaiting-payment')}
          </Button>
          <Button
            className='whitespace-nowrap !text-xxxs sm:!text-xxs'
            onClick={() => {
              deleteFilter('status');
              navigate(`detailed-order-status?status=EXPIRED`);
            }}
          >
            {t('dashboard.detailed.expired')}
          </Button>
        </ChartCardFooter>
      )}
    </ChartCard>
  );
};
